import React, { useEffect, useRef, useState } from "react";

interface AnimatedBackgroundProps {
  className?: string;
  children?: React.ReactNode;
  fullHeight?: boolean;
  baseColor?: string;      // Color for numbers and separators
  punchColor?: string;     // Color for punch symbols
}

interface PunchPosition {
  row: number;      // 0-8 (representing 1-9)
  col: number;      // Column index
  char: string;     // Which punch character (●, ■, or ▪)
  positionValue: number; // Normalized position for wave animation (0-1)
}

const AnimatedBackground: React.FunctionComponent<
  AnimatedBackgroundProps
> = ({ 
  className = "", 
  children, 
  fullHeight = true,
  baseColor = '#808080',
  punchColor,
}) => {
  // Use punchColor if provided, otherwise use baseColor
  const effectivePunchColor = punchColor || baseColor;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const punchPositionsRef = useRef<PunchPosition[]>([]);
  const cycleStartTimeRef = useRef<number>(0);
  const isInitializingRef = useRef<boolean>(true);
  const initStartTimeRef = useRef<number>(0);
  const isInitialRestRef = useRef<boolean>(false);
  const initialRestStartTimeRef = useRef<number>(0);
  const punchPositionsInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    // Subscribe to reduced motion preference changes
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size to match container
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      
      // Setting canvas.width/height automatically resets the context state
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      
      // Reapply scale after reset (ctx is the same object, just reset state)
      ctx.scale(dpr, dpr);
      
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();
    
    const handleResize = () => {
      resizeCanvas();
    };
    
    window.addEventListener("resize", handleResize);

    // Configuration
    const BASE_NUMBERS = 10; // Numbers 0-9
    const NUMBERS_PER_GROUP = 4;
    const SEPARATOR = '|';
    const PUNCH_CHAR = '█'; // ASCII 219
    const CHAR_SPACING = 2.5; // Spacious multiplier
    const FONT_SIZE = 14;
    const ROW_SPACING_MULTIPLIER = 1.8;
    
    // Animation timing
    const INIT_DURATION = 2000;         // 2 seconds - initial grid fade in
    const INITIAL_REST_DURATION = 10000; // 10 seconds - rest after initial render before punches start
    const PUNCH_DURATION = 6000;        // 6 seconds - punches appear
    const HOLD_DURATION = 10000;        // 10 seconds - punches stay visible
    const UNPUNCH_DURATION = 6000;      // 6 seconds - punches fade out
    const TOTAL_CYCLE = PUNCH_DURATION + HOLD_DURATION + UNPUNCH_DURATION;

    // Color and opacity configuration
    const BASE_COLOR = baseColor;      // Color for normal numbers and separators
    const PUNCH_COLOR = effectivePunchColor;     // Color for punch symbols
    const NORMAL_OPACITY = 0.25;     // Opacity for normal numbers
    const SEPARATOR_OPACITY = 0.25;  // Opacity for separators
    const PUNCH_OPACITY = 0.25;      // Opacity for punch symbols

    // Calculate grid dimensions
    const calculateGridDimensions = (width: number, height: number) => {
      ctx.font = `${FONT_SIZE}px "Geist Mono", monospace`;
      const charWidth = ctx.measureText("0").width;
      const cellWidth = charWidth * CHAR_SPACING;
      
      // Calculate how many columns fit (fill entire width edge-to-edge)
      // Calculate complete groups: each group has 4 numbers + 1 separator
      const groupWidth = cellWidth * (NUMBERS_PER_GROUP + 1); // 4 numbers + 1 separator
      let numGroups = Math.floor(width / groupWidth);
      
      // Check if we can fit one more complete group
      const usedWidth = numGroups * groupWidth;
      const remainingWidth = width - usedWidth;
      if (remainingWidth >= groupWidth) {
        numGroups++;
      }
      
      // Add extra columns to ensure edge-to-edge coverage (allows overflow at edges)
      // Calculate how many extra columns we can fit in remaining space
      const extraCols = Math.max(2, Math.ceil(remainingWidth / cellWidth));
      const totalCols = numGroups * NUMBERS_PER_GROUP + extraCols;
      const totalWidth = numGroups * cellWidth * NUMBERS_PER_GROUP + (numGroups - 1) * cellWidth;
      
      // Calculate how many rows fit (fill entire height)
      const rowHeight = FONT_SIZE * ROW_SPACING_MULTIPLIER;
      const rowsNeeded = Math.ceil(height / rowHeight);
      // Calculate how many rows per number (e.g., if 30 rows and 10 numbers = 3 rows each)
      const rowsPerNumber = Math.ceil(rowsNeeded / BASE_NUMBERS);
      const totalRows = rowsPerNumber * BASE_NUMBERS;
      
      return {
        charWidth,
        cellWidth,
        totalCols,
        numGroups,
        totalWidth,
        totalRows,
        rowHeight,
        rowsPerNumber,
      };
    };

    // Initialize punch positions for a new cycle
    const initializePunchPositions = (totalCols: number, totalRows: number) => {
      const positions: PunchPosition[] = [];
      const totalPositions = totalRows * totalCols;
      const punchCount = Math.floor(totalPositions * 0.025); // 2.5% of positions

      // Create array of all possible positions
      const allPositions: { row: number; col: number }[] = [];
      for (let row = 0; row < totalRows; row++) {
        for (let col = 0; col < totalCols; col++) {
          allPositions.push({ row, col });
        }
      }

      // Randomly select positions
      const shuffled = allPositions.sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, punchCount);

      // Create punch positions with normalized values for wave animation
      for (const pos of selected) {
        // Normalize to 0-1 range: average of row and column positions
        const positionValue = (pos.row / totalRows + pos.col / totalCols) / 2;
        positions.push({
          row: pos.row,
          col: pos.col,
          char: PUNCH_CHAR,
          positionValue: positionValue,
        });
      }

      return positions;
    };

    // Initialize timing refs
    const now = Date.now();
    if (cycleStartTimeRef.current === 0) cycleStartTimeRef.current = now;
    if (initStartTimeRef.current === 0) initStartTimeRef.current = now;
    if (initialRestStartTimeRef.current === 0) initialRestStartTimeRef.current = now;

    const rect = canvas.getBoundingClientRect();
    let gridDimensions = calculateGridDimensions(rect.width, rect.height);
    let lastKnownCols = gridDimensions.totalCols;
    let lastKnownRows = gridDimensions.totalRows;
    punchPositionsRef.current = initializePunchPositions(gridDimensions.totalCols, gridDimensions.totalRows);

    const animate = () => {
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      // Recalculate grid dimensions on each frame
      gridDimensions = calculateGridDimensions(width, height);
      
      // Check if dimensions changed and reinitialize punch positions only
      if (gridDimensions.totalCols !== lastKnownCols || gridDimensions.totalRows !== lastKnownRows) {
        lastKnownCols = gridDimensions.totalCols;
        lastKnownRows = gridDimensions.totalRows;
        // Only regenerate punch positions, maintain current animation phase
        punchPositionsRef.current = initializePunchPositions(gridDimensions.totalCols, gridDimensions.totalRows);
        punchPositionsInitializedRef.current = true;
      }

      // Clear canvas (transparent background)
      ctx.clearRect(0, 0, width, height);

      // Check if we're in initialization phase
      const initElapsed = Date.now() - initStartTimeRef.current;
      const isInitializing = isInitializingRef.current && initElapsed < INIT_DURATION;
      
      if (isInitializing) {
        // Initialization phase - fade in the grid
        const initProgress = initElapsed / INIT_DURATION;
        const initWaveProgress = initProgress;
        
        // Calculate positioning (no centering - fill from edge to edge)
        const { cellWidth, totalCols, totalRows, rowHeight } = gridDimensions;
        const offsetX = 0; // Start from left edge
        const offsetY = FONT_SIZE; // Start from top with baseline offset

        ctx.font = `${FONT_SIZE}px "Courier New", monospace`;

        // Draw grid with wave fade-in
        for (let row = 0; row < totalRows; row++) {
          const y = offsetY + row * rowHeight;
          // Calculate which number this row should display (1-9, then 0)
          const numberIndex = Math.floor(row / gridDimensions.rowsPerNumber);
          const number = numberIndex < 9 ? (numberIndex + 1).toString() : '0';

          let xPosition = offsetX;
          let colIndex = 0;
          
          // Draw all columns (groups + extra columns for edge coverage)
          while (colIndex < totalCols) {
            // Calculate position value for wave (normalized to 0-1)
            const positionValue = (row / totalRows + colIndex / totalCols) / 2;
            
            let numberAlpha = 0;
            if (initWaveProgress >= positionValue) {
              const fadeRange = 0.1;
              const fadeProgress = Math.min(1, (initWaveProgress - positionValue) / fadeRange);
              numberAlpha = fadeProgress;
            }

            if (numberAlpha > 0) {
              const baseRgb = parseInt(BASE_COLOR.slice(1), 16);
              const r = (baseRgb >> 16) & 255;
              const g = (baseRgb >> 8) & 255;
              const b = baseRgb & 255;
              ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${NORMAL_OPACITY * numberAlpha})`;
              ctx.fillText(number, xPosition, y);
            }

            xPosition += cellWidth;
            colIndex++;

            // Draw separator after every 4th column (except at the very end)
            if (colIndex % NUMBERS_PER_GROUP === 0 && colIndex < totalCols) {
              const sepPositionValue = (row / totalRows + colIndex / totalCols) / 2;
              let separatorAlpha = 0;
              if (initWaveProgress >= sepPositionValue) {
                const fadeRange = 0.1;
                const fadeProgress = Math.min(1, (initWaveProgress - sepPositionValue) / fadeRange);
                separatorAlpha = fadeProgress;
              }

              if (separatorAlpha > 0) {
                const baseRgb = parseInt(BASE_COLOR.slice(1), 16);
                const r = (baseRgb >> 16) & 255;
                const g = (baseRgb >> 8) & 255;
                const b = baseRgb & 255;
                ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${SEPARATOR_OPACITY * separatorAlpha})`;
                ctx.fillText(SEPARATOR, xPosition, y);
              }
              xPosition += cellWidth;
            }
          }
        }

        // Check if initialization is complete
        if (initElapsed >= INIT_DURATION) {
          isInitializingRef.current = false;
          isInitialRestRef.current = true;
          initialRestStartTimeRef.current = Date.now(); // Start initial rest timer
        }

        // Continue animation
        if (!prefersReducedMotion) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
        return;
      }

      // Check if we're in initial rest phase (after initialization, before first punch cycle)
      const initialRestElapsed = Date.now() - initialRestStartTimeRef.current;
      const isInInitialRest = isInitialRestRef.current && initialRestElapsed < INITIAL_REST_DURATION;
      
      if (isInInitialRest) {
        // Render static grid at full opacity during initial rest
        const { cellWidth, totalCols, totalRows, rowHeight } = gridDimensions;
        const offsetX = 0;
        const offsetY = FONT_SIZE;

        ctx.font = `${FONT_SIZE}px "Courier New", monospace`;

        // Draw static grid
        for (let row = 0; row < totalRows; row++) {
          const y = offsetY + row * rowHeight;
          const numberIndex = Math.floor(row / gridDimensions.rowsPerNumber);
          const number = numberIndex < 9 ? (numberIndex + 1).toString() : '0';

          let xPosition = offsetX;
          let colIndex = 0;
          
          // Draw all columns (groups + extra columns for edge coverage)
          while (colIndex < totalCols) {
            const baseRgb = parseInt(BASE_COLOR.slice(1), 16);
            const r = (baseRgb >> 16) & 255;
            const g = (baseRgb >> 8) & 255;
            const b = baseRgb & 255;
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${NORMAL_OPACITY})`;
            ctx.fillText(number, xPosition, y);

            xPosition += cellWidth;
            colIndex++;

            // Draw separator after every 4th column (except at the very end)
            if (colIndex % NUMBERS_PER_GROUP === 0 && colIndex < totalCols) {
              const baseRgb = parseInt(BASE_COLOR.slice(1), 16);
              const r = (baseRgb >> 16) & 255;
              const g = (baseRgb >> 8) & 255;
              const b = baseRgb & 255;
              ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${SEPARATOR_OPACITY})`;
              ctx.fillText(SEPARATOR, xPosition, y);
              xPosition += cellWidth;
            }
          }
        }

        // Check if initial rest is complete
        if (initialRestElapsed >= INITIAL_REST_DURATION) {
          isInitialRestRef.current = false;
          cycleStartTimeRef.current = Date.now(); // Start punch cycle timer
        }

        // Continue animation
        if (!prefersReducedMotion) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
        return;
      }

      // Normal animation phases
      const elapsed = Date.now() - cycleStartTimeRef.current;
      const cycleTime = elapsed % TOTAL_CYCLE;

      let phase: 'punching' | 'holding' | 'unpunching';
      let phaseProgress = 0;

      if (cycleTime < PUNCH_DURATION) {
        phase = 'punching';
        phaseProgress = cycleTime / PUNCH_DURATION;
        punchPositionsInitializedRef.current = false; // Reset flag at start of new cycle
      } else if (cycleTime < PUNCH_DURATION + HOLD_DURATION) {
        phase = 'holding';
        phaseProgress = (cycleTime - PUNCH_DURATION) / HOLD_DURATION;
      } else {
        phase = 'unpunching';
        phaseProgress = (cycleTime - PUNCH_DURATION - HOLD_DURATION) / UNPUNCH_DURATION;
        
        // Initialize new positions once at the end of unpunch phase (start of new cycle)
        if (cycleTime >= PUNCH_DURATION + HOLD_DURATION + UNPUNCH_DURATION - 100 && !punchPositionsInitializedRef.current) {
          punchPositionsRef.current = initializePunchPositions(gridDimensions.totalCols, gridDimensions.totalRows);
          punchPositionsInitializedRef.current = true;
        }
      }

      // Calculate wave progress (0 to 1 sweeping across grid)
      let waveProgress = 0;
      if (phase === 'punching') {
        waveProgress = phaseProgress;
      } else if (phase === 'holding') {
        waveProgress = 1;
      } else if (phase === 'unpunching') {
        waveProgress = phaseProgress; // Same direction sweep for unpunching
      }

      // Calculate positioning (no centering - fill from edge to edge)
      const { cellWidth, totalCols, totalRows, rowHeight } = gridDimensions;
      const offsetX = 0; // Start from left edge
      const offsetY = FONT_SIZE; // Start from top with baseline offset

      ctx.font = `${FONT_SIZE}px "Courier New", monospace`;

      // Create a map for quick punch lookup
      const punchMap = new Map<string, PunchPosition>();
      for (const punch of punchPositionsRef.current) {
        punchMap.set(`${punch.row}-${punch.col}`, punch);
      }

      // Draw grid
      for (let row = 0; row < totalRows; row++) {
        const y = offsetY + row * rowHeight;
        // Calculate which number this row should display (1-9, then 0)
        const numberIndex = Math.floor(row / gridDimensions.rowsPerNumber);
        const number = numberIndex < 9 ? (numberIndex + 1).toString() : '0';

        let xPosition = offsetX;
        let colIndex = 0;
        
        // Draw all columns (groups + extra columns for edge coverage)
        while (colIndex < totalCols) {
          // Check if this position should be punched
          const punchKey = `${row}-${colIndex}`;
          const punch = punchMap.get(punchKey);

          if (punch) {
            // Calculate punch visibility based on phase
            let punchAlpha = 0;
            
            if (phase === 'punching') {
              // Wave animation: punch becomes visible when wave reaches it
              if (waveProgress >= punch.positionValue) {
                // Smooth fade in over a small range
                const fadeRange = 0.05; // 5% of total progress
                const fadeProgress = Math.min(1, (waveProgress - punch.positionValue) / fadeRange);
                punchAlpha = fadeProgress;
              }
            } else if (phase === 'holding') {
              // Stay fully visible during hold phase
              punchAlpha = 1;
            } else if (phase === 'unpunching') {
              // Reverse wave animation: punch fades out when wave reaches it
              if (waveProgress >= punch.positionValue) {
                // Smooth fade out over a small range
                const fadeRange = 0.05; // 5% of total progress
                const fadeProgress = Math.min(1, (waveProgress - punch.positionValue) / fadeRange);
                punchAlpha = 1 - fadeProgress; // Reverse: start at 1, fade to 0
              } else {
                // Haven't reached this position yet, stay fully visible
                punchAlpha = 1;
              }
            }

            if (punchAlpha > 0) {
              // Draw punch character with lighter color
              const punchRgb = parseInt(PUNCH_COLOR.slice(1), 16);
              const r = (punchRgb >> 16) & 255;
              const g = (punchRgb >> 8) & 255;
              const b = punchRgb & 255;
              ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${PUNCH_OPACITY * punchAlpha})`;
              ctx.fillText(punch.char, xPosition, y);
              
              // Draw fading number underneath
              if (punchAlpha < 1) {
                const baseRgb = parseInt(BASE_COLOR.slice(1), 16);
                const br = (baseRgb >> 16) & 255;
                const bg = (baseRgb >> 8) & 255;
                const bb = baseRgb & 255;
                ctx.fillStyle = `rgba(${br}, ${bg}, ${bb}, ${NORMAL_OPACITY * (1 - punchAlpha)})`;
                ctx.fillText(number, xPosition, y);
              }
            } else {
              // Draw normal number
              const baseRgb = parseInt(BASE_COLOR.slice(1), 16);
              const r = (baseRgb >> 16) & 255;
              const g = (baseRgb >> 8) & 255;
              const b = baseRgb & 255;
              ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${NORMAL_OPACITY})`;
              ctx.fillText(number, xPosition, y);
            }
          } else {
            // Draw normal number
            const baseRgb = parseInt(BASE_COLOR.slice(1), 16);
            const r = (baseRgb >> 16) & 255;
            const g = (baseRgb >> 8) & 255;
            const b = baseRgb & 255;
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${NORMAL_OPACITY})`;
            ctx.fillText(number, xPosition, y);
          }

          xPosition += cellWidth;
          colIndex++;

          // Draw separator after every 4th column (except at the very end)
          if (colIndex % NUMBERS_PER_GROUP === 0 && colIndex < totalCols) {
            const baseRgb = parseInt(BASE_COLOR.slice(1), 16);
            const r = (baseRgb >> 16) & 255;
            const g = (baseRgb >> 8) & 255;
            const b = baseRgb & 255;
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${SEPARATOR_OPACITY})`;
            ctx.fillText(SEPARATOR, xPosition, y);
            xPosition += cellWidth;
          }
        }
      }

      // Continue animation if not reduced motion
      if (!prefersReducedMotion) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    // Start animation
    if (prefersReducedMotion) {
      // Render a static clean grid without animation
      isInitializingRef.current = false;
      isInitialRestRef.current = true;
      initialRestStartTimeRef.current = Date.now();
      animate();
    } else {
      // Start with initialization animation
      isInitializingRef.current = true;
      isInitialRestRef.current = false;
      initStartTimeRef.current = Date.now();
      animate();
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [prefersReducedMotion, baseColor, effectivePunchColor]);

  return (
    <section
      className={`relative w-full ${fullHeight ? 'min-h-screen' : ''} overflow-hidden ${className}`}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />
      {children && (
        <div className={`relative z-10 w-full ${fullHeight ? 'min-h-screen flex items-start justify-start pt-[20vh]' : ''}`}>
          {children}
        </div>
      )}
    </section>
  );
};

export default AnimatedBackground;
